import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import ContentContainer from 'components/content-container/ContentContainer';
import InfiniteTable from 'components/infinite-table/InfiniteTable';

import { detailedViewTable } from 'constants/market';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFiltersAllValues,
    selectMarketValue,
    selectMarketViewMajorThreshold,
    selectMarketViewMinorThreshold,
    selectMarketViewPeerComparisonChartData,
    selectMarketViewTopFiveEmergingMajorRmsData,
    selectMarketViewTopFiveEmergingModerateRmsData,
    selectMarketViewTopFiveRmsData,
    selectMarketViewTopFiveSpikeData,
    selectMarketViewTopFiveTeamsData,
    selectMarketViewTrendAnalysisChartData,
    selectRmValue,
    selectTeamDropdownValues,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerIsSameMonth,
    selectTimeslicerStartMonth,
    selectZoneValue
} from 'selectors/selectors';
import {
    fetchPeerComparison,
    fetchScenarioStats,
    fetchTopAndEmergingSpikes,
    fetchTopRanks,
    fetchTrendAnalysis
} from 'slices/MarketSlice';
import { fetchFilteredDropdownValues, updateFilter } from 'slices/FiltersSlice';
import {
    FilterType,
    RMCardGroupDataType,
    RMCardGroupTooltipType
} from 'types/types';
import {
    generateRequestParamsForMarketViewApi,
    generateRequestParamsForMarketViewFilterApi
} from 'utils/api';
import {
    marketAndTeamTop5ChartOptions,
    marketPeerComparisonChartOptions,
    marketTrendAnalysisChartOptions,
    scoreDecompositionChartOptions
} from 'utils/charts';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import RmCardsGroupSeries from 'components/rm-cards-group-series/RmCardsGroupSeries';
import {
    calculateLeftValueForGraphTooltip,
    calculateStartEndMonth,
    calculateTopValueForGraphTooltip,
    generateUpperRightDateRangeContent,
    isTypeNumber
} from 'utils/common';
import { SCENARIO_LIST } from 'constants/components';
import { useLocation } from 'react-router-dom';

import styles from './Market.module.scss';

const Market = ({ navigateTo }) => {
    const { pathname } = useLocation();
    const dispatch = useDispatch();

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);
    const isSameMonth = useSelector(selectTimeslicerIsSameMonth);
    const allValues = useSelector(selectFiltersAllValues);

    const dates = calculateStartEndMonth(tsStart, tsEnd, isSameMonth);
    const nonSpikeDates = calculateStartEndMonth(
        tsStart,
        tsEnd,
        isSameMonth,
        true
    );
    const { start, end } = { ...dates };
    const { start: nonSpikeStart, end: nonSpikeEnd } = { ...nonSpikeDates };

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const topFiveTeams = useSelector(selectMarketViewTopFiveTeamsData);
    const topFiveRms = useSelector(selectMarketViewTopFiveRmsData);
    const topFiveRmsSpike = useSelector(selectMarketViewTopFiveSpikeData);
    const topFiveEmergingModerateRms = useSelector(
        selectMarketViewTopFiveEmergingModerateRmsData
    );
    const topFiveEmergingMajorRms = useSelector(
        selectMarketViewTopFiveEmergingMajorRmsData
    );
    const peerComparisonChartData = useSelector(
        selectMarketViewPeerComparisonChartData
    );
    const trendAnalysisChartData = useSelector(
        selectMarketViewTrendAnalysisChartData
    );
    const minorThreshold = useSelector(selectMarketViewMinorThreshold);
    const majorThreshold = useSelector(selectMarketViewMajorThreshold);
    const teamDropdownValues = useSelector(selectTeamDropdownValues);

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipData, setTooltipData] = useState([]);
    const [chartsContainerHeight, setChartsContainerHeight] = useState(null);
    const [selectedDataIndex, setSelectedDataIndex] = useState(0);
    const [barValue, setBarValue] = useState(0);
    const [highestScore, setHighestScore] = useState(0);

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                const checkedMarketValue =
                    allValues.market.value === marketValue ? null : marketValue;
                const checkedTeamValue =
                    allValues.team.value === teamValue ? null : teamValue;
                const checkedRmValue =
                    allValues.rm.value === rmValue ? null : rmValue;

                // make sure to change team and rm values to 'ALL' value
                dispatch(
                    updateFilter({
                        value: allValues.team.value,
                        label: 'All',
                        filterType: FilterType.TEAM
                    })
                );

                dispatch(
                    updateFilter({
                        value: allValues.rm.value,
                        label: 'All',
                        filterType: FilterType.RM
                    })
                );

                dispatch(
                    fetchTopRanks(
                        generateRequestParamsForMarketViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            checkedTeamValue,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchTrendAnalysis(
                        generateRequestParamsForMarketViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            null,
                            null,
                            null,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchPeerComparison(
                        generateRequestParamsForMarketViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            null,
                            null,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchTopAndEmergingSpikes(
                        generateRequestParamsForMarketViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            checkedTeamValue,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchScenarioStats(
                        generateRequestParamsForMarketViewApi(
                            null,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
            }
        }
    }, [marketValue, teamValue, rmValue, tsStart, tsEnd]);

    useEffect(() => {
        const chartContainerElement = document.querySelector(
            'div[data-title^=charts]'
        );
        setChartsContainerHeight(chartContainerElement.offsetHeight);
    });

    const cardsDataArray = [
        {
            titleOne: 'Current Risk',
            titleTwo: 'Current Risk',
            subTitleOne: '- By Total Risk Score',
            subTitleTwo: '- By Highest Spike',
            upperRightContentOne: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                shouldShowSameWhenOneMonth: true
            }),
            upperRightContentTwo: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            footerOne: 'Total Risk Score',
            footerTwo: ['Total Risk Score', '(Spike)'],
            groupOneData: topFiveRms,
            groupTwoData: topFiveRmsSpike,
            groupOneDataType: RMCardGroupDataType.TopFiveRms,
            groupTwoDataType: RMCardGroupDataType.TopFiveRmsSpike,
            groupOneTooltipType: RMCardGroupTooltipType.ScoreDecomposition,
            groupTwoTooltipType: RMCardGroupTooltipType.TrendAnalysis,
            isBottomGroup: false
        },
        {
            titleOne: 'Emerging Risk',
            titleTwo: 'Emerging Risk',
            subTitleOne: '- Red Threshold Breach Prediction',
            subTitleTwo: '- Amber Threshold Breach Prediction',
            upperRightContentOne: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            upperRightContentTwo: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            footerOne: 'Total Risk Score',
            footerTwo: 'Total Risk Score',
            groupOneData: topFiveEmergingMajorRms,
            groupTwoData: topFiveEmergingModerateRms,
            groupOneDataType: RMCardGroupDataType.TopFiveEmergingMajorRm,
            groupTwoDataType: RMCardGroupDataType.TopFiveEmergingModerateRm,
            groupOneTooltipType: RMCardGroupTooltipType.Default,
            groupTwoTooltipType: RMCardGroupTooltipType.Default,
            isBottomGroup: true
        }
    ];

    const onChartNodeMouseOver = (param) => {
        const { data, name, dataIndex } = param;
        const dataToUse = topFiveTeams;
        const dataToPassToTooltip = dataToUse.find(
            (data) => data.dimension === name
        );
        setBarValue(data);
        setTooltipData(dataToPassToTooltip);
        setSelectedDataIndex(dataIndex);
        setHighestScore(Math.max(...dataToUse.map((d) => d.score)));
        setShowTooltip(true);
    };

    const onChartNodeMouseOut = () => {
        setShowTooltip(false);
    };

    const onClickTop5 = (param) => {
        const team = teamDropdownValues.find((r) => r.label === param.name);

        dispatch(
            updateFilter({
                value: team.value,
                label: team.label,
                filterType: FilterType.TEAM
            })
        );

        const requestParamsObj = generateRequestParamsForMarketViewFilterApi(
            tsStart ? tsStart.epoch : null,
            tsEnd ? tsEnd.epoch : null,
            zoneValue,
            team.value
        );
        const enrichedParamsObj = {
            requestParams: {
                ...requestParamsObj.requestParams,
                selectedFilterType: FilterType.TEAM,
                label: team.label,
                value: team.value
            }
        };

        dispatch(fetchFilteredDropdownValues(enrichedParamsObj));

        navigateTo(pathname, '/team');
    };

    const onEvents = {
        mouseover: onChartNodeMouseOver,
        mouseout: onChartNodeMouseOut,
        click: onClickTop5
    };

    const onClickPeerComparison = (param) => {
        const { name, seriesIndex } = param;

        SCENARIO_LIST.map((scenario) => {
            if (name === scenario.name && seriesIndex === 0)
                navigateTo(pathname, scenario.path);
        });
    };

    const onPeerComparisonEvents = {
        click: onClickPeerComparison
    };

    const memoizedTeamTop5Chart = useMemo(() => {
        return (
            <ReactECharts
                option={marketAndTeamTop5ChartOptions(
                    topFiveTeams,
                    'Top 5 Teams - By Team Risk Score',
                    'market'
                )}
                style={{ width: '550px', height: '100%' }}
                onEvents={onEvents}
            ></ReactECharts>
        );
    }, [topFiveTeams]);

    const chartRef = useRef(null);
    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.getEchartsInstance().dispose();
            chartRef.current
                .getEchartsInstance()
                .setOption(
                    marketTrendAnalysisChartOptions(trendAnalysisChartData)
                );
        }
    }, [trendAnalysisChartData]);

    const memoizedPeerComparisonChart = useMemo(() => {
        return (
            <ReactECharts
                option={marketPeerComparisonChartOptions(
                    peerComparisonChartData
                )}
                style={{ width: '100%', height: '100%' }}
                onEvents={onPeerComparisonEvents}
            ></ReactECharts>
        );
    }, [peerComparisonChartData]);

    const isTooltipContainsFiveBar =
        tooltipData &&
        tooltipData.s1Score > 0 &&
        tooltipData.s2Score > 0 &&
        tooltipData.s3Score > 0 &&
        tooltipData.s4Score > 0 &&
        tooltipData.correlationScore > 0;

    return (
        <ContentContainer>
            <div className={styles.chartsContainer} data-title={'charts'}>
                <ChartWrapper comboClass={styles.rightMargin}>
                    {memoizedTeamTop5Chart}
                </ChartWrapper>
                {showTooltip && (
                    <ChartWrapper
                        comboClass={styles.absolutePosition}
                        style={{
                            top: `${calculateTopValueForGraphTooltip(
                                topFiveTeams.length,
                                chartsContainerHeight,
                                selectedDataIndex
                            )}px`,
                            left: `${calculateLeftValueForGraphTooltip(
                                barValue,
                                highestScore
                            )}%`
                        }}
                        width={'320px'}
                        height={'auto'}
                    >
                        <ReactECharts
                            option={scoreDecompositionChartOptions(tooltipData)}
                            style={
                                isTooltipContainsFiveBar
                                    ? { width: '300px', height: '330px' }
                                    : { width: '300px' }
                            }
                        ></ReactECharts>
                    </ChartWrapper>
                )}

                <ChartWrapper comboClass={styles.rightMargin}>
                    <ReactECharts
                        option={marketTrendAnalysisChartOptions(
                            trendAnalysisChartData
                        )}
                        style={{ width: '100%', height: '100%' }}
                        ref={chartRef}
                    ></ReactECharts>
                </ChartWrapper>

                <ChartWrapper>{memoizedPeerComparisonChart}</ChartWrapper>
            </div>

            <RmCardsGroupSeries
                seriesTitle={'Top 5 RMs'}
                minorThreshold={minorThreshold}
                majorThreshold={majorThreshold}
                cardsDataArray={cardsDataArray}
                navigateTo={navigateTo}
            />

            <div className={styles.tableContainer}>
                <div className={styles.tableHeader}>
                    <div className={styles.title}>Detailed View</div>
                </div>
                <InfiniteTable
                    zone={zoneValue}
                    market={marketValue}
                    team={teamValue}
                    rm={rmValue}
                    start={tsStart}
                    end={tsEnd}
                    allValues={allValues}
                    columns={detailedViewTable}
                />
            </div>
        </ContentContainer>
    );
};

Market.propTypes = {
    navigateTo: PropTypes.func
};
export default Market;
